/**
 * ThemeContext — light / dark mode for the CFO365.ai web app.
 *
 * How it works:
 *   - The design system (src/index.css) declares all colors as CSS custom
 *     properties under :root (light) and .dark (dark). Toggling the `dark`
 *     class on <html> swaps every token at once — no re-render, no per-component
 *     work. See tailwind.config.js (darkMode: 'class').
 *   - This context owns that class: it persists the user's choice to
 *     localStorage and reflects it onto document.documentElement.
 *   - public/index.html runs a tiny inline script BEFORE React mounts that
 *     applies the saved (or default) theme, so the correct background paints
 *     immediately with no flash. This provider reads the class that script set,
 *     so React state and the DOM never disagree on first paint.
 *
 * Default: dark (black background) when the user has no saved preference.
 */

import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'punch-theme';
const DEFAULT_THEME = 'dark';

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  toggleTheme: () => {},
  setTheme: () => {},
});

// Resolve the theme to use on first render. Prefer what the inline boot script
// already applied to <html> (source of truth for first paint), then storage,
// then the default. Guarded for SSR / non-browser contexts.
function getInitialTheme() {
  if (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')) {
    return 'dark';
  }
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') return stored;
    } catch (_) {
      /* localStorage unavailable (private mode) — fall through */
    }
  }
  // If <html> has no `dark` class and nothing stored, we're in light.
  if (typeof document !== 'undefined') {
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  }
  return DEFAULT_THEME;
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  // Keep the mobile browser chrome / address bar in sync with the surface.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'dark' ? '#121212' : '#F8FAFC');
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);

  // Reflect every change onto <html> and persist it.
  useEffect(() => {
    applyTheme(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch (_) {
      /* ignore persistence failures */
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next === 'dark' ? 'dark' : 'light');
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export default ThemeContext;
